import { Box } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useResizeDetector } from "react-resize-detector";

import { useAppSelector } from "~/app/store";

import {
  selectAndonBoardIconScale,
  selectCurrentAndonBoardView,
  selectEditModeEnabled,
  selectSelectedAndonLayoutName,
  selectFetchStatusEndpointsInterval,
  selectHandRaisedByIdMap,
  setBoardIconScale,
  setBoardPortToWorkstationId,
  clearBoardPortToWorkstationId
} from "~/features/andon/andon.slice";
import {
  buildWorkstationFcAbbreviationById,
  isBoardLayoutVisibleToUser,
  resolveBoardFetchFcIds
} from "~/features/andon/andon.utils";
import { useAndonWorkstationsByFcIds } from "~/hooks/useAndonWorkstationsByFcIds";
import { useClientConfigETagFlow } from "~/lib/clientConfigHelpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectAuth0UserId,
  selectUserIsAdmin
} from "~/redux/selectors/authSelectors";
import { useToggleHandRaiseMutation } from "~/redux/warehouse/andon.hooks";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";
import { AndonWorkstation } from "~/types/api";

import { buildBoardPortToWorkstationId } from "./andonBoardLayout.utils";
import AndonBoardGrid from "./components/AndonBoardGrid";
import AndonBoardToolbar from "./components/AndonBoardToolbar";
import { LayoutEditorDrawer } from "./components/LayoutEditorDrawer";
import LowerHandDialog from "./components/LowerHandDialog";
import { SaveLayoutModal } from "./components/SaveLayoutModal";
import { TextLabelModal } from "./components/TextLabelModal";
import { useAndonAdvancedFeatures } from "./hooks/useAndonAdvancedFeatures";
import { useAndonBoardEditSelection } from "./hooks/useAndonBoardEditSelection";
import {
  AndonBoardEditSelectionBridge,
  useAndonBoardLayout
} from "./hooks/useAndonBoardLayout";
import { useAndonBoardSignalRListen } from "./hooks/useAndonBoardSignalRListen";

export default function AndonBoard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { errorToast, successToast } = useToast();
  const { saveConfig } = useClientConfigETagFlow();
  const advanced = useAndonAdvancedFeatures();
  const auth0UserId = useAppSelector(selectAuth0UserId);

  const {
    width: boardWidth,
    height: boardHeight,
    ref: boardRef
  } = useResizeDetector();

  const clientConfig = useAppSelector(selectServerConfig);
  const { an_boardLayouts } = clientConfig;
  const visibleBoardLayouts = useMemo(
    () =>
      an_boardLayouts.filter((layout) =>
        isBoardLayoutVisibleToUser(layout, {
          advanced,
          userId: auth0UserId
        })
      ),
    [an_boardLayouts, advanced, auth0UserId]
  );
  const selectedLayoutName = useAppSelector(selectSelectedAndonLayoutName);
  const siteFulfillmentCenterId = useAppSelector(
    (state) => state.workstations.siteFulfillmentCenterId
  );
  const boardIconScale = useAppSelector(selectAndonBoardIconScale);
  const andonViewOption = useAppSelector(selectCurrentAndonBoardView);
  const editModeEnabled = useAppSelector(selectEditModeEnabled);
  const fetchWorkstationsInterval = useAppSelector(
    selectFetchStatusEndpointsInterval
  );
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const handRaisedByIdMap = useAppSelector(selectHandRaisedByIdMap);

  const isFullScreen = andonViewOption === "board fullscreen";

  const [saveLayoutModalOpen, setSaveLayoutModalOpen] = useState(false);
  const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
  const [lowerHandDialogOpen, setLowerHandDialogOpen] = useState(false);
  const [workstationToLowerHand, setWorkstationToLowerHand] = useState<{
    id: string;
    deviceId: string;
    autostoreGridId: string;
  } | null>(null);

  // Latest selection snapshot for layout mutations (avoids layout↔selection hook cycle).
  const editSelectionRef = useRef<AndonBoardEditSelectionBridge>({
    closeEditCellMenu: () => {},
    selectedGridIndex: null,
    setSelectedGridIndex: () => {},
    editCellMenuAnchorPosition: null,
    borderTargetCells: []
  });
  const workstationsRef = useRef<AndonWorkstation[] | undefined>(undefined);

  const {
    workingLayout,
    setWorkingLayout,
    savedLayout,
    workingLayoutItems,
    boardDimensions,
    boardScale,
    boardDisplayLines,
    cellBorders,
    workingLayoutName,
    workingLayoutFcIds,
    hasLayoutChanges,
    loadWorkingLayout,
    handleDimensionChange,
    handleSaveLayout,
    handleDeleteLayout,
    handleDiscardChanges,
    handleClearLayout,
    removeWorkstation,
    selectWorkstation,
    handlePickWorkstation,
    handleAddWorkstations,
    editingTextLabel,
    textLabelModalOpen,
    openAddTextLabelModal,
    handleAddTextLabel,
    handleEditTextLabel,
    handleDeleteTextLabel,
    closeTextLabelModal,
    handleSetCellBorder,
    workingWorkstationIds,
    minBoardDimensions
  } = useAndonBoardLayout({
    editSelectionRef,
    boardWidth,
    boardHeight,
    workstationsRef,
    siteFulfillmentCenterId,
    an_boardLayouts,
    selectedLayoutName,
    clientConfig,
    saveConfig,
    advanced,
    userId: auth0UserId
  });

  const gridHeightPercentage = 100 / boardDimensions.rows;
  const gridRowHeight = (boardHeight || 0) * (gridHeightPercentage / 100) - 1;

  const layoutFcIds = useMemo(
    () =>
      resolveBoardFetchFcIds({
        layoutLevelFcIds: workingLayoutFcIds,
        layoutItems: workingLayoutItems
      }),
    [workingLayoutFcIds, workingLayoutItems]
  );
  useAndonBoardSignalRListen(layoutFcIds);
  const workstationFetchFcIds = useMemo(
    () =>
      layoutFcIds.length === 1 && layoutFcIds[0] === siteFulfillmentCenterId
        ? []
        : layoutFcIds,
    [layoutFcIds, siteFulfillmentCenterId]
  );

  const { data: allFcs = [] } = useGetAllFulfillmentCentersQuery();
  const { workstations, isError: workstationsError } =
    useAndonWorkstationsByFcIds(workstationFetchFcIds, { poll: true });
  const [toggleHandRaise, { isLoading: isLoweringHand }] =
    useToggleHandRaiseMutation();

  workstationsRef.current = workstations;

  useEffect(() => {
    if (!workstations) {
      dispatch(clearBoardPortToWorkstationId());
      return;
    }

    const portToWorkstationId = buildBoardPortToWorkstationId(workstations);
    dispatch(setBoardPortToWorkstationId(portToWorkstationId));
  }, [workstations, dispatch]);

  const boardWorkstationsReady =
    workstations !== undefined || workstationsError;

  const workstationFcAbbreviationById = useMemo(
    () =>
      buildWorkstationFcAbbreviationById(
        workstations,
        allFcs,
        siteFulfillmentCenterId
      ),
    [workstations, allFcs, siteFulfillmentCenterId]
  );

  const {
    selectedGridIndex,
    setSelectedGridIndex,
    isMarqueeDragging,
    editCellMenuAnchorPosition,
    isEditCellMenuOpen,
    isMultiCellSelection,
    selectionHighlightCells,
    borderTargetCells,
    targetCellSides,
    targetCellColor,
    pickerSelectionTitle,
    isTextLabelSelected,
    selectSingleCell,
    closeEditCellMenu,
    handleGridClick,
    handleBoardMouseDown
  } = useAndonBoardEditSelection({
    editModeEnabled,
    advanced,
    boardWidth,
    boardHeight,
    boardDimensions,
    gridRowHeight,
    workingLayoutItems,
    cellBorders,
    workstations
  });

  editSelectionRef.current = {
    closeEditCellMenu,
    selectedGridIndex,
    setSelectedGridIndex,
    editCellMenuAnchorPosition,
    borderTargetCells
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>, index: string) => {
    if (editModeEnabled) {
      const boardLayoutItem = workingLayoutItems.find(
        (item) => item.i === index
      );
      if (!boardLayoutItem) {
        return;
      }
      const cell = { x: boardLayoutItem.x, y: boardLayoutItem.y };
      selectSingleCell(cell, {
        tileId: index,
        clientX: event.clientX,
        clientY: event.clientY
      });
      return;
    }

    if (isAdmin) {
      const handRaiseState = handRaisedByIdMap[index];
      if (handRaiseState?.raised) {
        const workstation = workstations?.find(
          (candidateWorkstation) => candidateWorkstation.workstationId === index
        );
        if (workstation) {
          setWorkstationToLowerHand({
            id: workstation.workstationId,
            deviceId: workstation.deviceId,
            autostoreGridId: workstation.autostoreGridId
          });
          setLowerHandDialogOpen(true);
        }
      }
    }
  };

  const handleExternalLowerHand = async () => {
    if (!workstationToLowerHand) return;

    try {
      await toggleHandRaise({
        autostoreGridId: workstationToLowerHand.autostoreGridId,
        workstationId: workstationToLowerHand.id,
        raiseHand: false
      }).unwrap();
      successToast(
        t("hand lowered for workstation {{deviceId}}", {
          deviceId: workstationToLowerHand.deviceId
        })
      );
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    } finally {
      setLowerHandDialogOpen(false);
      setWorkstationToLowerHand(null);
    }
  };

  const handleCancelLowerHand = () => {
    setLowerHandDialogOpen(false);
    setWorkstationToLowerHand(null);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        px: isFullScreen ? 0 : 4
      }}
    >
      <AndonBoardToolbar
        isFullScreen={isFullScreen}
        editModeEnabled={editModeEnabled}
        an_boardLayouts={visibleBoardLayouts}
        selectedLayoutName={selectedLayoutName}
        workingLayoutItems={workingLayoutItems}
        workingLayoutName={workingLayoutName}
        savedLayout={savedLayout}
        hasLayoutChanges={hasLayoutChanges}
        fetchWorkstationsInterval={fetchWorkstationsInterval}
        workingWorkstationIds={workingWorkstationIds}
        siteFulfillmentCenterId={siteFulfillmentCenterId}
        loadWorkingLayout={loadWorkingLayout}
        handleDeleteLayout={handleDeleteLayout}
        handleSaveLayout={handleSaveLayout}
        handleAddWorkstations={handleAddWorkstations}
        handleClearLayout={handleClearLayout}
        handleDiscardChanges={handleDiscardChanges}
        onOpenLayoutEditor={() => setLayoutEditorOpen(true)}
        onOpenSaveLayoutModal={() => setSaveLayoutModalOpen(true)}
      />

      <AndonBoardGrid
        boardRef={boardRef}
        editModeEnabled={editModeEnabled}
        advanced={advanced}
        isMarqueeDragging={isMarqueeDragging}
        boardWidth={boardWidth}
        boardHeight={boardHeight}
        gridRowHeight={gridRowHeight}
        boardDimensions={boardDimensions}
        cellBorders={cellBorders}
        selectionHighlightCells={selectionHighlightCells}
        workingLayoutItems={workingLayoutItems}
        boardScale={boardScale}
        boardIconScale={boardIconScale}
        boardDisplayLines={boardDisplayLines}
        boardWorkstationsReady={boardWorkstationsReady}
        workstations={workstations}
        workstationFcAbbreviationById={workstationFcAbbreviationById}
        setWorkingLayout={setWorkingLayout}
        onBoardMouseDown={handleBoardMouseDown}
        onExistingElementClick={handleClick}
        onGridClick={handleGridClick}
        removeWorkstation={removeWorkstation}
        isMultiCellSelection={isMultiCellSelection}
        isTextLabelSelected={isTextLabelSelected}
        isEditCellMenuOpen={isEditCellMenuOpen}
        editCellMenuAnchorPosition={editCellMenuAnchorPosition}
        selectedGridIndex={selectedGridIndex}
        targetCellSides={targetCellSides}
        targetCellColor={targetCellColor}
        pickerSelectionTitle={pickerSelectionTitle}
        workingWorkstationIds={workingWorkstationIds}
        siteFcId={siteFulfillmentCenterId ?? undefined}
        closeEditCellMenu={closeEditCellMenu}
        handleSetCellBorder={handleSetCellBorder}
        handleEditTextLabel={handleEditTextLabel}
        handleDeleteTextLabel={handleDeleteTextLabel}
        selectWorkstation={selectWorkstation}
        handlePickWorkstation={handlePickWorkstation}
        openAddTextLabelModal={openAddTextLabelModal}
      />
      <SaveLayoutModal
        open={saveLayoutModalOpen}
        onClose={() => setSaveLayoutModalOpen(false)}
        onSave={handleSaveLayout}
        existingNames={an_boardLayouts.map((layout) => layout.name)}
        currentLayoutName={savedLayout?.name ?? selectedLayoutName}
      />
      <LayoutEditorDrawer
        open={layoutEditorOpen}
        onClose={() => setLayoutEditorOpen(false)}
        columns={boardDimensions.columns}
        rows={boardDimensions.rows}
        minColumns={minBoardDimensions.columns}
        minRows={minBoardDimensions.rows}
        scale={boardScale}
        iconScale={boardIconScale}
        displayLines={boardDisplayLines}
        layoutName={workingLayoutName}
        showPrivateCheckbox={advanced}
        isPrivate={workingLayout.isPrivate}
        onColumnsChange={(columns) =>
          handleDimensionChange(columns, boardDimensions.rows)
        }
        onRowsChange={(rows) =>
          handleDimensionChange(boardDimensions.columns, rows)
        }
        onScaleChange={(scale) =>
          setWorkingLayout((currentWorkingLayout) => ({
            ...currentWorkingLayout,
            scale
          }))
        }
        onIconScaleChange={(scale: number) =>
          dispatch(setBoardIconScale(scale))
        }
        onDisplayLinesChange={(displayLines) =>
          setWorkingLayout((currentWorkingLayout) => ({
            ...currentWorkingLayout,
            displayLines
          }))
        }
        onLayoutNameChange={(newName) => {
          setWorkingLayout((currentWorkingLayout) => ({
            ...currentWorkingLayout,
            name: newName
          }));
        }}
        onPrivateChange={(isPrivate) => {
          setWorkingLayout((currentWorkingLayout) => ({
            ...currentWorkingLayout,
            isPrivate,
            ownerUserId: isPrivate ? (auth0UserId ?? null) : null
          }));
        }}
      />
      <TextLabelModal
        open={textLabelModalOpen}
        onClose={closeTextLabelModal}
        onSave={handleAddTextLabel}
        existingElement={editingTextLabel ?? undefined}
      />

      <LowerHandDialog
        open={lowerHandDialogOpen}
        deviceId={workstationToLowerHand?.deviceId ?? ""}
        isLoweringHand={isLoweringHand}
        onCancel={handleCancelLowerHand}
        onConfirm={handleExternalLowerHand}
      />
    </Box>
  );
}
